import React, { useState, useRef, useEffect } from 'react';
import { HiArrowRight, HiArrowLeft, HiCamera, HiPhoto } from 'react-icons/hi2';
import { FiTrash2, FiUpload } from 'react-icons/fi';
import { uploadDeviceImage, getDeviceImages, deleteDeviceImage } from '../utils/api';

export default function InstallationPhotoStep({ onNext, onBack, deviceId }) {
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');

  const [cameraActive, setCameraActive] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!deviceId) { setLoadingExisting(false); return; }
    getDeviceImages(deviceId)
      .then(data => {
        const items = Array.isArray(data)
          ? data.map(item => typeof item === 'string'
              ? { imageUrl: item, imageId: null }
              : { imageUrl: item.imageUrl, imageId: item.imageId }
            ).filter(i => i.imageUrl)
          : [];
        setExistingImages(items);
      })
      .catch(() => {/* silently ignore — no existing images to show */})
      .finally(() => setLoadingExisting(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const handleDeleteExisting = async (imageId, idx) => {
    if (!imageId) return;
    try {
      await deleteDeviceImage(deviceId, imageId);
      setExistingImages(prev => prev.filter((_, i) => i !== idx));
    } catch (err) {
      console.error('Failed to delete image:', err);
      setError('Failed to delete image. Please try again.');
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
      images.forEach(img => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const waitForVideoReady = (videoEl) =>
    new Promise((resolve, reject) => {
      if (!videoEl) return reject(new Error('No video element'));
      if (videoEl.readyState >= 2 && videoEl.videoWidth) return resolve();
      const onReady = () => {
        videoEl.removeEventListener('canplay', onReady);
        videoEl.removeEventListener('loadeddata', onReady);
        resolve();
      };
      videoEl.addEventListener('canplay', onReady, { once: true });
      videoEl.addEventListener('loadeddata', onReady, { once: true });
    });

  // both for camera and upload from gallery 
  const startCamera = async () => {
    try {
      setError('');
      setCameraReady(false);
      setCameraActive(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (err) {
          console.debug('video.play() ignored:', err);
        }
        await waitForVideoReady(videoRef.current);
        setCameraReady(true);
      }
    } catch (err) {
      console.error(err);
      setError('Unable to access camera. Please allow permission or upload from gallery instead.');
      setCameraActive(false);
      setCameraReady(false);
    }
  };

  const stopCamera = () => {
    try {
      const stream = videoRef.current?.srcObject;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    } catch (e) {
      console.debug('stopCamera ignored:', e);
    }
    setCameraActive(false);
    setCameraReady(false);
  };

  const compressImage = (file, maxWidth = 1920, quality = 0.8) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('Compression failed')),
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
      img.src = url;
    });

  const captureFrameToBlob = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      throw new Error('Camera not available');
    }

    if (video.readyState < 2 || !video.videoWidth) {
      await waitForVideoReady(video);
    }

    const MAX_WIDTH = 1920;
    const scale = Math.min(1, MAX_WIDTH / video.videoWidth);
    const ctx = canvas.getContext('2d');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => {
          if (!b) return reject(new Error('Could not create image blob'));
          resolve(b);
        },
        'image/jpeg',
        0.8
      );
    });

    return blob;
  };

  const capturePhoto = async () => {
    try {
      setError('');
      const blob = await captureFrameToBlob();
      const file = new File([blob], `installation-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const preview = URL.createObjectURL(file);

      setImages(prev => [...prev, { file, preview, uploaded: false }]);
      stopCamera();
    } catch (e) {
      console.error(e);
      setError(e.message || 'Camera not ready yet. Try again.');
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    setError('');
    const newImages = await Promise.all(
      files.map(async (file) => {
        const compressed = await compressImage(file);
        const compressedFile = new File([compressed], file.name, { type: 'image/jpeg' });
        return { file: compressedFile, preview: URL.createObjectURL(compressedFile), uploaded: false };
      })
    );
    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    const img = images[index];
    if (img.preview) URL.revokeObjectURL(img.preview);
    setImages(images.filter((_, i) => i !== index));
  };

  const uploadAllImages = async () => {
    if (images.length === 0) {
      setError('Please add at least one installation photo.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      let successCount = 0;
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        if (img.uploaded) continue; 

        setUploadProgress(`Uploading ${i + 1} of ${images.length}...`);

        try {
          await uploadDeviceImage(deviceId, img.file);
          // store or mark it as already uploaded 
          setImages(prev => {
            const updated = [...prev];
            updated[i].uploaded = true;
            return updated;
          });
          successCount++;
        } catch (err) {
          console.error(`Failed to upload image ${i + 1}:`, err);
          setError(`Failed to upload image ${i + 1}. Please try again.`);
          setUploading(false);
          setUploadProgress('');
          return;
        }
      }

      setUploadProgress(`Successfully uploaded ${successCount} photo(s)!`);
      setTimeout(() => {
        onNext();
      }, 1000);
    } catch (err) {
      console.error(err);
      setError('Upload failed. Please try again.');
      setUploading(false);
      setUploadProgress('');
    }
  };

  const skipStep = () => {
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">Installation Photos</h1>
        <p className="text-sm text-gray-600 mt-1">
          Take photos of the installed device
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={startCamera}
          disabled={uploading}
          className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition disabled:opacity-50 text-sm"
        >
          <HiCamera className="w-5 h-5" />
          Take Photo
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition disabled:opacity-50 text-sm"
        >
          <HiPhoto className="w-5 h-5" />
          Upload from Gallery
        </button>
      </div>

      {/* Camera Preview */}
      {cameraActive && (
        <div className="space-y-3 border rounded-lg p-4 bg-gray-50">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full rounded-md border bg-black"
            onLoadedMetadata={() => {
              if (videoRef.current) {
                videoRef.current.play().catch((err) =>
                  console.debug('video.play() on metadata ignored:', err)
                );
              }
            }}
            onCanPlay={() => setCameraReady(true)}
          />
          <canvas ref={canvasRef} className="hidden" />

          <div className="flex gap-3">
            <button
              onClick={capturePhoto}
              disabled={!cameraReady}
              className={`flex-1 px-4 py-2 rounded-lg text-sm text-white ${
                !cameraReady
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {cameraReady ? 'Capture Photo' : 'Initializing...'}
            </button>

            <button
              onClick={stopCamera}
              className="px-4 py-2 rounded-lg text-sm bg-gray-200 text-gray-800 hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Existing Uploaded Images */}
      {loadingExisting ? (
        <p className="text-sm text-gray-500">Loading existing photos...</p>
      ) : existingImages.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Already Uploaded ({existingImages.length})
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {existingImages.map((item, idx) => (
              <div key={idx} className="relative group">
                <img
                  src={item.imageUrl}
                  alt={`Existing ${idx + 1}`}
                  className="w-full h-32 object-cover border rounded-lg bg-gray-100"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div
                  className="w-full h-32 border rounded-lg bg-gray-100 items-center justify-center text-xs text-gray-400 flex-col gap-1"
                  style={{ display: 'none' }}
                >
                  <span>⚠️</span>
                  <span>Image unavailable</span>
                </div>
                <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                  Uploaded
                </div>
                {item.imageId && (
                  <button
                    onClick={() => handleDeleteExisting(item.imageId, idx)}
                    disabled={uploading}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow hover:bg-red-600 opacity-0 group-hover:opacity-100 transition disabled:opacity-50"
                    title="Delete"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Gallery */}
      {images.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Photos ({images.length})
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {images.map((img, idx) => (
              <div key={idx} className="relative group">
                <img
                  src={img.preview}
                  alt={`Installation ${idx + 1}`}
                  className="w-full h-32 object-cover border rounded-lg"
                />
                {img.uploaded && (
                  <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                    ✓ Uploaded
                  </div>
                )}
                <button
                  onClick={() => removeImage(idx)}
                  disabled={uploading}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow hover:bg-red-600 opacity-0 group-hover:opacity-100 transition disabled:opacity-50"
                  title="Remove"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {uploadProgress && (
        <p className="text-sm text-primary font-medium">{uploadProgress}</p>
      )}

      {/* Error Message */}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Navigation Buttons */}
      <div className="pt-6 flex justify-between">
        <button
          onClick={onBack}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gray-200 text-sm text-gray-800 hover:bg-gray-300 transition disabled:opacity-50"
        >
          <HiArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex gap-3">
          <button
            onClick={skipStep}
            disabled={uploading}
            className="px-5 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
          >
            Skip
          </button>
          <button
            onClick={uploadAllImages}
            disabled={uploading || images.length === 0}
            className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition disabled:opacity-50"
          >
            {uploading ? (
              <>
                <FiUpload className="w-4 h-4 animate-pulse" />
                Uploading...
              </>
            ) : (
              <>
                Submit & Continue
                <HiArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
